import React, { useState } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  IconButton,
  Divider,
  Chip,
  Tooltip,
  CircularProgress,
  Alert,
  Snackbar
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Cable as CableIcon,
  Download as DownloadIcon
} from '@mui/icons-material';
import { formatPrice } from '../services/pricingService';
import { exportCableSVG, canGenerateSVG } from '../services/customerSvgExportService';
import { compatibilityMatrix } from '../services/compatibilityMatrix';
import CompactPriceDisplay from './CompactPriceDisplay';
import { buildFinishingDisplayRows } from '../utils/finishingDisplayRows';
import FinishingDetailRows from './FinishingDetailRows';

// Helper function to check if connector is XLR 5-pin
function isXLR5Pin(connector) {
  return connector?.id === 'NC5FXX' || connector?.id === 'NC5MXX';
}

function CartItemsView({ cart, onRemoveFromCart }) {
  const safeCart = cart || [];
  const [exporting, setExporting] = useState({});
  const [exportMessage, setExportMessage] = useState('');
  const [exportSuccess, setExportSuccess] = useState(false);

  const calculateTotal = () => {
    return safeCart.reduce((total, item) => total + (item.price || 0), 0);
  };

  const handleExportSVG = async (item, index) => {
    if (!canGenerateSVG(item)) {
      setExportMessage('SVG export is not available for this cable configuration');
      setExportSuccess(false);
      return;
    }

    setExporting(prev => ({ ...prev, [index]: true }));
    try {
      const result = await exportCableSVG(item);
      setExportMessage(`SVG exported successfully: ${result.filename}`);
      setExportSuccess(true);
    } catch (error) {
      console.error('Export error:', error);
      setExportMessage('Failed to export SVG. Please try again.');
      setExportSuccess(false);
    } finally {
      setExporting(prev => ({ ...prev, [index]: false }));
    }
  };

  if (safeCart.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="text.secondary" variant="h6">
          Your cart is empty
        </Typography>
        <Typography color="text.secondary" variant="body2" sx={{ mt: 1 }}>
          Add cables to your cart to see them here
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <List>
        {safeCart.map((item, index) => (
          <React.Fragment key={index}>
            <ListItem
              alignItems="flex-start"
              sx={{ 
                pr: 4, // Add more right padding to prevent text from getting too close to action buttons
                gap: 2 // Add gap between avatar and text content
              }}
              secondaryAction={
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  minWidth: '120px', // Ensure minimum width for action area
                  ml: 2 // Add left margin to separate from text content
                }}>
                  <Box sx={{ mr: 2, textAlign: 'right', minWidth: '80px' }}>
                    <CompactPriceDisplay config={item} showIcon={false} variant="h6" />
                  </Box>
                  {canGenerateSVG(item) && (
                    <Tooltip title="Download SVG diagram">
                      <IconButton
                        onClick={() => handleExportSVG(item, index)}
                        disabled={exporting[index]}
                        sx={{ mr: 1 }}
                      >
                        {exporting[index] ? (
                          <CircularProgress size={20} />
                        ) : (
                          <DownloadIcon />
                        )}
                      </IconButton>
                    </Tooltip>
                  )}
                  <IconButton
                    edge="end"
                    onClick={() => onRemoveFromCart(index)}
                    color="error"
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              }
            >
              <ListItemAvatar sx={{ mr: 2 }}>
                <Avatar
                  variant="rounded"
                  sx={{
                    width: { xs: 96, sm: 70 },
                    height: { xs: 72, sm: 50 },
                    cursor: item.diagramUrl ? 'pointer' : 'default',
                    border: item.diagramUrl ? '2px solid #1976d2' : '2px solid #e0e0e0',
                    backgroundColor: 'white',
                    '&:hover': item.diagramUrl ? {
                      border: '2px solid #1565c0',
                      transform: 'scale(1.05)',
                      transition: 'all 0.2s ease-in-out',
                      boxShadow: '0 4px 8px rgba(0,0,0,0.2)'
                    } : {}
                  }}
                  onClick={item.diagramUrl ? () => window.open(item.diagramUrl, '_blank') : undefined}
                >
                  {item.diagramUrl ? (
                                                    <img
                                  src={item.diagramUrl}
                                  alt="Cable Diagram"
                                  style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'contain',
                                    borderRadius: '8px'
                                  }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  <Box
                    sx={{
                      width: '100%',
                      height: '100%',
                      display: item.diagramUrl ? 'none' : 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: '#f5f5f5',
                      borderRadius: '8px',
                      color: '#666'
                    }}
                  >
                    <CableIcon sx={{ fontSize: 20 }} />
                  </Box>
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                sx={{
                  mr: 2, // Add right margin to prevent text from getting too close to action area
                  minWidth: 0, // Allow text to shrink if needed
                  flex: 1 // Allow text to take available space
                }}
                primary={
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                      {item.name || 'NLFX Custom Cable'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      SKU: {item.sku || 'Pending Configuration'}
                    </Typography>
                  </Box>
                }
                secondary={
                  <Box sx={{ mt: 1, pr: 2 }}>
                    <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <span style={{ marginRight: '4px' }}>
                        {(() => {
                          console.log('DEBUG: Cart item display - item:', item);
                          console.log('DEBUG: Cart item display - item.style:', item.style);
                          console.log('DEBUG: Cart item display - item.length:', item.length);
                          console.log('DEBUG: Cart item display - item.hybridConnectors:', item.hybridConnectors);
                          
                          // For hybrid/zip cables, show overall length from hybridConnectors
                          if (item.style === 'hybrid' || item.style === 'zip' || item.style === 'zip-audio-audio' || item.style === 'wye' || item.style === 'hybrid-power-audio-audio') {
                            const overallLength = item.hybridConnectors?.lengths?.Overall;
                            if (overallLength) {
                              const calculatedLength = Math.round(overallLength / 12 * 100) / 100;
                              console.log('DEBUG: Cart item display - hybrid length calculation:', { overallLength, calculatedLength });
                              return `${calculatedLength} ft`;
                            }
                          }
                          
                          // For standard/end-to-end cables, use item.length
                          if (['standard', 'end-to-end'].includes(item.style) || !item.style) {
                            console.log('DEBUG: Cart item display - standard length:', item.length);
                            return `${item.length} ft`;
                          }
                          
                          // Fallback: use item.length
                          console.log('DEBUG: Cart item display - fallback length:', item.length);
                          return `${item.length} ft`;
                        })()}
                      </span>
                      <Tooltip
                        title="Cable length measured to center of connector, exact length may vary"
                        arrow
                        placement="top"
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            color: '#ff6600',
                            cursor: 'help',
                            fontWeight: 'bold',
                            mr: 1
                          }}
                        >
                          *
                        </Typography>
                      </Tooltip>
                      <span>- {item.quality || 'Standard'}</span>
                    </Typography>
                    <Typography 
                      variant="body2" 
                      color="text.secondary"
                      sx={{
                        wordBreak: 'break-word', // Allow long connector names to wrap
                        lineHeight: 1.4, // Improve line spacing for readability
                        maxWidth: '100%' // Ensure text doesn't overflow
                      }}
                    >
                      {(() => {
                        // Helper function for configuration abbreviation
                        const getConfigAbbrev = (configuration) => {
                          if (!configuration) return '';
                          const config = configuration.toLowerCase();
                          if (config === 'standard' || config === 'std') return 'STD';
                          if (config === 'right-angle' || config === 'right angle' || config === 'ra') return 'RA';
                          if (config === 'panel-mount' || config === 'panel mount' || config === 'panel') return 'P';
                          return '';
                        };

                        const formatConnector = (end) => {
                          if (!end?.connector && !end?.label) return '';
                          let str = '';
                          if (end.brand) str += end.brand + ' ';
                          str += end.connector || end.label || '';

                          // Add XLR 5-pin asterisk
                          if (isXLR5Pin(end)) {
                            str += '*';
                          }

                          // Add configuration abbreviation
                          const configAbbrev = getConfigAbbrev(end.variant || end.configuration);
                          if (configAbbrev) {
                            str += ` (${configAbbrev})`;
                          } else if (end.variant && end.variant !== 'standard') {
                            let variantLabel = end.variant;
                            if (variantLabel === 'panel-mount') variantLabel = 'Panel-Mount';
                            else if (variantLabel === 'inline') variantLabel = 'Inline';
                            else variantLabel = variantLabel.replace('-', ' ').replace(/\b\w/g, c => c.toUpperCase());
                            str += ` (${variantLabel})`;
                          }
                          return str;
                        };

                        // Handle hybrid/zip cables
                        if (item.style === 'hybrid' || item.style === 'zip' || item.style === 'zip-audio-audio' || item.style === 'wye' || item.style === 'hybrid-power-audio-audio') {
                          const hybridConnectors = item.hybridConnectors?.connectors || {};
                          const connectorLabels = Object.keys(hybridConnectors).filter(key => hybridConnectors[key]?.label);
                          if (connectorLabels.length > 0) {
                            return connectorLabels.map(key => {
                              const connector = hybridConnectors[key];
                              const configAbbrev = getConfigAbbrev(connector.configuration);
                              const xlrAsterisk = isXLR5Pin(connector) ? '*' : '';
                              // Get the proper description from the compatibility matrix
                              let connectorName = '';
                              if (connector?.id) {
                                const matrixEntry = compatibilityMatrix[connector.id];
                                if (matrixEntry?.description) {
                                  connectorName = matrixEntry.description;
                                } else if (connector.description) {
                                  connectorName = connector.description;
                                } else if (connector.label) {
                                  connectorName = connector.label;
                                } else {
                                  connectorName = connector.id;
                                }
                              } else {
                                connectorName = connector.label || connector.description || connector.id || '';
                              }
                              return `${key}: ${connectorName}${xlrAsterisk}${configAbbrev ? ` (${configAbbrev})` : ''}`;
                            }).join(', ');
                          }
                          return 'Hybrid/Zip Cable';
                        }

                        // Handle standard cables
                        if (item.endA?.connector && item.endB?.connector) {
                          return `${formatConnector(item.endA)} to ${formatConnector(item.endB)}`;
                        } else if (item.endA?.connector) {
                          return formatConnector(item.endA);
                        } else if (item.endB?.connector) {
                          return formatConnector(item.endB);
                        } else {
                          return '';
                        }
                      })()}
                    </Typography>

                    {/* Diagram link and filename */}
                    {item.diagramUrl && (
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          <a href={item.diagramUrl} target="_blank" rel="noopener noreferrer" style={{ wordBreak: 'break-all' }}>
                            View full diagram
                          </a>
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          File: {item.diagramUrl.split('/').pop()}
                        </Typography>
                      </Box>
                    )}

                    {/* Wire Gauge for power cables */}
                    {item.type === 'power' && item.wireGauge && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        Wire: {item.wireGauge.gauge} AWG, {item.wireGauge.color} jacket
                      </Typography>
                    )}

                    {/* Finishing details */}
                    {item.finishing && (
                      <Box sx={{ mt: 1 }}>
                        <FinishingDetailRows
                          rows={buildFinishingDisplayRows(item.finishing, { cartDefaultCodingRings: true })}
                          muted
                        />
                      </Box>
                    )}

                    {/* Special Instructions */}
                    {(item.specialInstructions || item.hybridConnectors?.specialInstructions) && (
                      <Box sx={{
                        mt: 1,
                        p: 1,
                        backgroundColor: '#f8f9fa',
                        borderRadius: 1,
                        border: '1px solid #e9ecef'
                      }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                          Special Instructions:
                        </Typography>
                        <Typography variant="body2" sx={{
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                          color: '#495057',
                          mt: 0.5
                        }}>
                          {item.specialInstructions || item.hybridConnectors?.specialInstructions}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                }
              />
            </ListItem>
            {index < safeCart.length - 1 && <Divider />}
          </React.Fragment>
        ))}
      </List>

      {/* Total */}
      <Box sx={{ mt: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
        <Typography variant="h5" align="right" sx={{ fontWeight: 'bold' }}>
          Total: {formatPrice(calculateTotal())}
        </Typography>
      </Box>

      <Snackbar
        open={!!exportMessage}
        autoHideDuration={6000}
        onClose={() => setExportMessage('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setExportMessage('')}
          severity={exportSuccess ? 'success' : 'error'}
          sx={{ width: '100%' }}
        >
          {exportMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default CartItemsView; 